/* eslint-disable no-console */
import {
  ConrepAppWorkday,
  ConrepAppWorkdayNumbers,
  ConrepAppWorkdayElements,
  ConrepAppWorkdayData,
  ReviewStatus,
  ConrepAppWorkdayReview,
  ConrepAppWorkdayMessage,
  WorkdayCacheConfig,
  WorkdayCacheEntry,
  CacheStats,
} from './types';
import { api } from 'src/common/fetch';

/** Simple IndexedDB wrapper for cache storage */
const DB_NAME = 'SitelifeWorkdayCache';
const DB_VERSION = 1;
const STORE_NAME = 'cache_entries';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });

  return dbPromise;
}

async function idbGet(key: string): Promise<string | null> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);

    return new Promise((resolve, reject) => {
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result || null);
    });
  } catch (error) {
    console.error('IndexedDB get error:', error);
    return null;
  }
}

async function idbSet(key: string, value: string): Promise<boolean> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(value, key);

    return new Promise((resolve, reject) => {
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(true);
    });
  } catch (error) {
    console.error('IndexedDB set error:', error);
    return false;
  }
}

async function idbRemove(key: string): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.delete(key);
  } catch (error) {
    console.error('IndexedDB remove error:', error);
  }
}

async function idbGetAllKeys(): Promise<string[]> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAllKeys();

    return new Promise((resolve, reject) => {
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result as string[]);
    });
  } catch (error) {
    console.error('IndexedDB getAllKeys error:', error);
    return [];
  }
}

/*
async function idbClear(): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.clear();
  } catch (error) {
    console.error('IndexedDB clear error:', error);
  }
}
*/

/** Workday Cache Configuration */
export const DEFAULT_CACHE_CONFIG: WorkdayCacheConfig = {
  maxCacheSize: 100 * 1024 * 1024, // 100MB in bytes
  cacheExpirationDays: 7,
  recentDaysDefault: 40,
  enableLogging: true,
  minWorkdaysForCache: 100,
};

// Allow runtime configuration override
let cacheConfig = { ...DEFAULT_CACHE_CONFIG };

// Test configuration to bypass IndexedDB for testing data merging logic
let testConfig = {
  bypassIndexedDB: false,
  mockLoadCacheEntry: null as
    | ((
        projectId: string,
        techCrewId: string,
      ) => Promise<WorkdayCacheEntry | null>)
    | null,
};

function setTestConfig(config: Partial<typeof testConfig>): void {
  testConfig = { ...testConfig, ...config };
}

export function setCacheConfig(config: Partial<WorkdayCacheConfig>): void {
  cacheConfig = { ...cacheConfig, ...config };
}

export function getCacheConfig(): WorkdayCacheConfig {
  return { ...cacheConfig };
}

/** Cache Utility Functions */

// const CACHE_VERSION = '3.0.0'; // Updated for IndexedDB support
const CACHE_KEY_PREFIX = 'sitelife_workday_cache'; // Updated for IndexedDB
const CACHE_STATS_KEY = 'sitelife_cache_stats';

function getCacheKey(projectId: string, techCrewId: string): string {
  return `${CACHE_KEY_PREFIX}_${projectId}_${techCrewId}`;
}

function logCache(message: string, data?: any): void {
  if (cacheConfig.enableLogging) {
    console.log(`[WorkdayCache] ${message}`, data || '');
  }
}

function getCacheStats(): CacheStats {
  try {
    const statsStr = localStorage.getItem(CACHE_STATS_KEY);
    if (statsStr) {
      return JSON.parse(statsStr);
    }
  } catch (error) {
    logCache('Error loading cache stats', error);
  }
  return {
    totalSize: 0,
    totalEntries: 0,
    hitCount: 0,
    missCount: 0,
    evictionCount: 0,
  };
}

function saveCacheStats(stats: CacheStats): void {
  try {
    localStorage.setItem(CACHE_STATS_KEY, JSON.stringify(stats));
  } catch (error) {
    logCache('Error saving cache stats', error);
  }
}

function updateCacheStats(update: Partial<CacheStats>): void {
  const currentStats = getCacheStats();
  const newStats = { ...currentStats, ...update };
  saveCacheStats(newStats);
}

async function loadCacheEntry(
  projectId: string,
  techCrewId: string,
): Promise<WorkdayCacheEntry | null> {
  if (testConfig.bypassIndexedDB) {
    logCache(
      `Bypassing IndexedDB for loadCacheEntry due to testConfig.bypassIndexedDB`,
    );
    return testConfig.mockLoadCacheEntry?.(projectId, techCrewId) || null;
  }

  try {
    const cacheKey = getCacheKey(projectId, techCrewId);
    const cacheStr = await idbGet(cacheKey);

    if (!cacheStr) {
      logCache(`Cache miss for ${projectId}/${techCrewId}`);
      updateCacheStats({ missCount: getCacheStats().missCount + 1 });
      return null;
    }

    let cacheEntry: WorkdayCacheEntry;

    try {
      cacheEntry = JSON.parse(cacheStr);
      logCache(
        `Cache entry loaded successfully for ${projectId}/${techCrewId}`,
      );
    } catch (parseError) {
      logCache(
        `Failed to parse cache entry for ${projectId}/${techCrewId}`,
        parseError,
      );
      await idbRemove(cacheKey);
      updateCacheStats({ missCount: getCacheStats().missCount + 1 });
      return null;
    }

    if (!isCacheValid(cacheEntry)) {
      logCache(`Cache invalid for ${projectId}/${techCrewId}, removing`);
      await idbRemove(cacheKey);
      updateCacheStats({ missCount: getCacheStats().missCount + 1 });
      return null;
    }

    logCache(`Cache hit for ${projectId}/${techCrewId}`, {
      olderWorkdays: cacheEntry.olderWorkdayIds.length,
      lastUpdated: new Date(cacheEntry.lastUpdated).toISOString(),
    });
    updateCacheStats({ hitCount: getCacheStats().hitCount + 1 });
    return cacheEntry;
  } catch (error) {
    logCache(`Error loading cache for ${projectId}/${techCrewId}`, error);
    updateCacheStats({ missCount: getCacheStats().missCount + 1 });
    return null;
  }
}

async function saveCacheEntry(entry: WorkdayCacheEntry): Promise<boolean> {
  if (testConfig.bypassIndexedDB) {
    logCache(
      `Bypassing IndexedDB for saveCacheEntry due to testConfig.bypassIndexedDB`,
    );
    return true; // Mock success
  }

  try {
    const cacheKey = getCacheKey(entry.projectId, entry.techCrewId);

    // Serialize to JSON
    const jsonStr = JSON.stringify(entry);
    const entrySize = new Blob([jsonStr]).size;

    // Check if adding this entry would exceed cache size limit
    const currentStats = getCacheStats();
    if (currentStats.totalSize + entrySize > cacheConfig.maxCacheSize) {
      logCache(`Cache size limit would be exceeded, evicting old entries`);
      await evictOldestCaches();
    }

    // Save to IndexedDB
    const success = await idbSet(cacheKey, jsonStr);

    if (!success) {
      logCache(`Failed to save cache entry`, {
        entrySize,
        maxCacheSize: cacheConfig.maxCacheSize,
      });
      return false;
    }

    // Update cache stats
    updateCacheStats({
      totalSize: currentStats.totalSize + entrySize,
      totalEntries: currentStats.totalEntries + 1,
    });

    logCache(`Cache saved for ${entry.projectId}/${entry.techCrewId}`, {
      entrySize,
      olderWorkdays: entry.olderWorkdayIds.length,
    });
    return true;
  } catch (error) {
    logCache(
      `Error saving cache for ${entry.projectId}/${entry.techCrewId}`,
      error,
    );
    return false;
  }
}

function isCacheValid(entry: WorkdayCacheEntry): boolean {
  // Check cache version
  /*
  if (entry.cacheVersion !== CACHE_VERSION) {
    logCache('Cache version mismatch', {
      cached: entry.cacheVersion,
      current: CACHE_VERSION
    });
    return false;
  }
  */

  // Check expiration
  const expirationTime =
    entry.lastUpdated + cacheConfig.cacheExpirationDays * 24 * 60 * 60 * 1000;
  const isExpired = Date.now() > expirationTime;

  if (isExpired) {
    logCache('Cache expired', {
      lastUpdated: new Date(entry.lastUpdated).toISOString(),
      expirationTime: new Date(expirationTime).toISOString(),
    });
    return false;
  }

  return true;
}

export async function clearWorkdayCache(
  projectId?: string,
  techCrewId?: string,
): Promise<void> {
  if (testConfig.bypassIndexedDB) {
    logCache(
      `Bypassing IndexedDB for clearWorkdayCache due to testConfig.bypassIndexedDB`,
    );
    return;
  }

  try {
    if (projectId && techCrewId) {
      // Clear specific cache entry
      const cacheKey = getCacheKey(projectId, techCrewId);
      await idbRemove(cacheKey);
      logCache(`Cleared cache for ${projectId}/${techCrewId}`);
    } else {
      // Clear all workday caches
      const allKeys = await idbGetAllKeys();
      const keysToRemove = allKeys.filter((key) =>
        key.startsWith(CACHE_KEY_PREFIX),
      );

      for (const key of keysToRemove) {
        await idbRemove(key);
      }
      logCache(`Cleared ${keysToRemove.length} cache entries`);
    }

    // Reset cache stats
    saveCacheStats({
      totalSize: 0,
      totalEntries: 0,
      hitCount: 0,
      missCount: 0,
      evictionCount: 0,
    });
  } catch (error) {
    logCache('Error clearing cache', error);
  }
}

async function evictOldestCaches(
  percentageToEvict: number = 0.25,
): Promise<void> {
  if (testConfig.bypassIndexedDB) {
    logCache(
      `Bypassing IndexedDB for evictOldestCaches due to testConfig.bypassIndexedDB`,
    );
    return;
  }

  try {
    const cacheEntries: { key: string; lastUpdated: number }[] = [];

    // Collect all cache entries with their timestamps
    const allKeys = await idbGetAllKeys();
    const cacheKeys = allKeys.filter((key) => key.startsWith(CACHE_KEY_PREFIX));

    for (const key of cacheKeys) {
      try {
        const cacheStr = await idbGet(key);
        if (cacheStr) {
          const entry: WorkdayCacheEntry = JSON.parse(cacheStr);
          cacheEntries.push({ key, lastUpdated: entry.lastUpdated });
        }
      } catch (error) {
        // Invalid cache entry, add to removal list
        cacheEntries.push({ key, lastUpdated: 0 });
      }
    }

    // Sort by lastUpdated (oldest first)
    cacheEntries.sort((a, b) => a.lastUpdated - b.lastUpdated);

    // Remove oldest entries based on percentage
    const entriesToRemove = Math.max(
      1,
      Math.floor(cacheEntries.length * percentageToEvict),
    );
    for (let i = 0; i < entriesToRemove; i++) {
      await idbRemove(cacheEntries[i].key);
    }

    // Recalculate cache stats after eviction
    const newCacheSize = await getCacheSize();
    const remainingEntries = cacheEntries.length - entriesToRemove;

    updateCacheStats({
      evictionCount: getCacheStats().evictionCount + entriesToRemove,
      totalSize: newCacheSize,
      totalEntries: remainingEntries,
    });

    logCache(`Evicted ${entriesToRemove} oldest cache entries`, {
      percentageEvicted: `${(percentageToEvict * 100).toFixed(1)}%`,
      remainingEntries,
      newCacheSizeBytes: newCacheSize,
    });
  } catch (error) {
    logCache('Error during cache eviction', error);
  }
}

function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
}

async function getCacheSize(): Promise<number> {
  if (testConfig.bypassIndexedDB) {
    logCache(
      `Bypassing IndexedDB for getCacheSize due to testConfig.bypassIndexedDB`,
    );
    return 0; // Mock size
  }

  try {
    let totalSize = 0;
    const allKeys = await idbGetAllKeys();
    const cacheKeys = allKeys.filter((key) => key.startsWith(CACHE_KEY_PREFIX));

    for (const key of cacheKeys) {
      const value = await idbGet(key);
      if (value) {
        totalSize += new Blob([value]).size;
      }
    }
    return totalSize;
  } catch (error) {
    logCache('Error calculating cache size', error);
    return 0;
  }
}

// Note: Compression stats removed since we're no longer using compression with IndexedDB

/** Helper Functions */

function separateElementsByDate(
  allElements: ConrepAppWorkdayElements,
  recentDaysCount: number,
  workdayNumbers: ConrepAppWorkdayNumbers,
): {
  recentElements: ConrepAppWorkdayElements;
  olderElements: ConrepAppWorkdayElements;
  cutoffDate: string;
} {
  // Sort workdays by date (newest first)
  const sortedWorkdays = [...workdayNumbers].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  // Determine cutoff date (the date of the 50th most recent workday)
  const cutoffDate =
    sortedWorkdays[Math.min(recentDaysCount - 1, sortedWorkdays.length - 1)]
      ?.date || '';

  // Create sets of recent and older workday IDs
  const recentWorkdayIds = new Set(
    sortedWorkdays.slice(0, recentDaysCount).map((wd) => wd._id),
  );
  const olderWorkdayIds = new Set(
    sortedWorkdays.slice(recentDaysCount).map((wd) => wd._id),
  );

  const recentElements: ConrepAppWorkdayElements = {};
  const olderElements: ConrepAppWorkdayElements = {};

  // Separate elements based on their workday
  Object.entries(allElements).forEach(([elementId, actions]) => {
    const recentActions = actions.filter((action) => {
      const workdayId =
        typeof action.workday === 'string'
          ? action.workday
          : action.workday._id;
      return recentWorkdayIds.has(workdayId);
    });

    const olderActions = actions.filter((action) => {
      const workdayId =
        typeof action.workday === 'string'
          ? action.workday
          : action.workday._id;
      return olderWorkdayIds.has(workdayId);
    });

    if (recentActions.length > 0) {
      recentElements[elementId] = recentActions;
    }

    if (olderActions.length > 0) {
      olderElements[elementId] = olderActions;
    }
  });

  return { recentElements, olderElements, cutoffDate };
}

async function updateCache(
  projectId: string,
  techCrewId: string,
  allElements: ConrepAppWorkdayElements,
  recentDaysCount: number,
): Promise<void> {
  if (testConfig.bypassIndexedDB) {
    logCache(
      `Bypassing IndexedDB for updateCache due to testConfig.bypassIndexedDB`,
    );
    return;
  }

  try {
    // Get workday numbers to determine what's recent vs older
    const workdayNumbers = await getWorkdayNumbers(projectId, techCrewId, true);
    if (
      !workdayNumbers ||
      workdayNumbers.length <= cacheConfig.minWorkdaysForCache
    ) {
      logCache(`Not enough workdays to cache (${workdayNumbers?.length || 0})`);
      return;
    }

    const { olderElements, cutoffDate } = separateElementsByDate(
      allElements,
      recentDaysCount,
      workdayNumbers,
    );

    // Sort workdays by date (oldest first for cache storage)
    const sortedWorkdays = [...workdayNumbers].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );

    // Get older workday IDs (exclude recent ones)
    const olderWorkdayIds = sortedWorkdays
      .slice(0, Math.max(0, sortedWorkdays.length - recentDaysCount))
      .map((wd) => wd._id);

    // we can add cacheVersion: CACHE_VERSION, but we don't need it for now
    const cacheEntry: WorkdayCacheEntry = {
      projectId,
      techCrewId,
      olderElements,
      olderWorkdayIds,
      recentCutoffDate: cutoffDate,
      lastUpdated: Date.now(),
      totalWorkdaysWhenCached: workdayNumbers.length,
    };

    const success = await saveCacheEntry(cacheEntry);
    if (success) {
      logCache(`Cache updated for ${projectId}/${techCrewId}`, {
        olderWorkdays: olderWorkdayIds.length,
        recentWorkdays: recentDaysCount,
        totalWorkdays: workdayNumbers.length,
      });
    }
  } catch (error) {
    logCache(`Error updating cache for ${projectId}/${techCrewId}`, error);
  }
}

async function getTotalWorkdayCount(
  projectId: string,
  techCrewId: string,
): Promise<number> {
  try {
    const workdayNumbers = await getWorkdaysCountForProjectAndTechCrew(
      projectId,
      techCrewId,
    );
    return workdayNumbers || 0;
  } catch (error) {
    logCache(
      `Error getting total workday count for ${projectId}/${techCrewId}`,
      error,
    );
    return 0;
  }
}

export async function getWorkdaysCountForProjectAndTechCrew(
  projectId: string,
  techCrewId: string,
) {
  return api.get<number>(
    `/api-conrep/projects/${projectId}/techcrews/${techCrewId}/workdays/count`,
  );
}

export async function getWorkdayNumbers(
  projectId: string,
  techCrewId: string,
  short?: boolean,
) {
  let queryParams = '';
  if (short === true) {
    queryParams = '?numbers=true&info=true';
  }
  return api.get<ConrepAppWorkdayNumbers>(
    `/api-conrep/projects/${projectId}/techcrews/${techCrewId}/workdays${queryParams}`,
  );
}

export async function getWorkday(
  projectId: string,
  techCrewId: string,
  wdId: string,
  onlyData?: boolean,
) {
  let queryString =
    '?elementActions=true&screenshots=true&nonModeledElements=true&regies=true&documents=true';
  if (onlyData) {
    queryString = '';
  }
  return api.get<ConrepAppWorkday>(
    `/api-conrep/projects/${projectId}/techcrews/${techCrewId}/workdays/${wdId}${queryString}`,
  );
}

export async function getElementsByTechcrew(
  projectId: string,
  techCrewId: string,
  useCache?: boolean,
) {
  let queryParams = '';
  if (useCache === true) {
    queryParams = '?useCache=true';
  }
  return api.get<ConrepAppWorkdayElements>(
    `/api-conrep/projects/${projectId}/techcrews/${techCrewId}/workdays/null/actions${queryParams}`,
  );
}

export async function getProjectTechCrewWorkdays(
  projectId: string,
  techCrewId: string,
  nrOfWorkdays?: number,
  all?: boolean,
) {
  let queryParams = `?limit=${nrOfWorkdays}`;
  if (all === true) {
    queryParams = '?all=true';
  }
  return api.get<ConrepAppWorkdayElements>(
    `/api-conrep/projects/${projectId}/techcrews/${techCrewId}/workdays/null/actions/recent${queryParams}`,
  );
}

export async function updateWorkdayData(
  projectId: string,
  tcId: string,
  wdId: string,
  workdayData: { data: Partial<ConrepAppWorkdayData> },
) {
  return api.post(
    `/api-conrep/projects/${projectId}/techcrews/${tcId}/workdays/${wdId}`,
    workdayData,
  );
}

export async function deleteWorkdays(
  projectId: string,
  techCrewId: string,
  wdIds: string[],
) {
  return api.delete(
    `/api-conrep/projects/${projectId}/techcrews/${techCrewId}/workdays`,
    wdIds,
  );
}

export async function lockWorkdays(
  projectId: string,
  techCrewId: string,
  wdIds: string[],
) {
  return api.post<{ success: string[]; failure: string[] }>(
    `/api-conrep/projects/${projectId}/techcrews/${techCrewId}/workdays/lock`,
    wdIds,
  );
}

export async function unlockWorkdays(
  projectId: string,
  techCrewId: string,
  startDay: string,
) {
  return api.post<{ success: string[]; failure: string[] }>(
    `/api-conrep/projects/${projectId}/techcrews/${techCrewId}/workdays/unlock`,
    { date: startDay },
  );
}

export async function createWorkday(
  projectId: string,
  techCrewId: string,
  initialData: Partial<ConrepAppWorkday>,
) {
  return api.post<ConrepAppWorkday>(
    `/api-conrep/projects/${projectId}/techcrews/${techCrewId}/workdays`,
    initialData,
  );
}

export async function addWorkdayReview(
  projectId: string,
  techCrewId: string,
  workdayId: string,
  status: ReviewStatus,
) {
  return api.post<ConrepAppWorkdayReview>(
    `/api-conrep/projects/${projectId}/techcrews/${techCrewId}/workdays/${workdayId}/reviews`,
    { status },
  );
}

export async function addWorkdayMessage(
  projectId: string,
  techCrewId: string,
  workdayId: string,
  text: string,
) {
  return api.post<{ message: ConrepAppWorkdayMessage; didRevert: boolean }>(
    `/api-conrep/projects/${projectId}/techcrews/${techCrewId}/workdays/${workdayId}/messages`,
    { text },
  );
}

export async function submitWorkdayForApproval(
  projectId: string,
  techCrewId: string,
  workdayId: string,
  lang: string,
) {
  return api.post<ConrepAppWorkdayReview>(
    `/api-conrep/projects/${projectId}/techcrews/${techCrewId}/workdays/${workdayId}/submit?lang=${lang}`,
  );
}

/** Main Caching Function */

export async function getElementsByTechcrew_New(
  projectId: string,
  techCrewId: string,
  recentDaysCount: number = cacheConfig.recentDaysDefault,
  useCache: boolean = true,
): Promise<ConrepAppWorkdayElements> {
  const startTime = Date.now();

  logCache(
    `Starting getElementsByTechcrew_New for project ${projectId}, techCrew ${techCrewId}`,
    {
      recentDaysCount,
      useCache,
    },
  );

  try {
    // Check localStorage availability first (skip if bypassing IndexedDB)
    if (!testConfig.bypassIndexedDB && !isLocalStorageAvailable()) {
      logCache(`LocalStorage not available, bypassing cache`);
      useCache = false;
    }

    // Check if we should use caching
    const totalWorkdays = await getTotalWorkdayCount(projectId, techCrewId);

    if (totalWorkdays <= cacheConfig.minWorkdaysForCache || !useCache) {
      logCache(`Bypassing cache`, {
        reason:
          totalWorkdays <= cacheConfig.minWorkdaysForCache
            ? 'insufficient workdays'
            : 'cache disabled',
        totalWorkdays,
        useCache,
      });

      // Use original function or new API to get all data
      const allElements = await getProjectTechCrewWorkdays(
        projectId,
        techCrewId,
        undefined,
        true,
      );

      const totalTime = Date.now() - startTime;
      logCache(`Completed without cache`, {
        totalElements: Object.keys(allElements).length,
        totalTimeMs: totalTime,
      });

      return allElements;
    }

    // Log cache stats at start (skip if bypassing IndexedDB)
    if (!testConfig.bypassIndexedDB) {
      const cacheStats = getCacheStats();
      const currentCacheSize = await getCacheSize();
      logCache(`Cache stats`, {
        ...cacheStats,
        currentSizeBytes: currentCacheSize,
        maxSizeBytes: cacheConfig.maxCacheSize,
        utilization: `${(
          (currentCacheSize / cacheConfig.maxCacheSize) *
          100
        ).toFixed(1)}%`,
      });
    }

    // Get recent workdays from server
    const fetchStartTime = Date.now();
    const recentElements = await getProjectTechCrewWorkdays(
      projectId,
      techCrewId,
      recentDaysCount,
    );
    const fetchTime = Date.now() - fetchStartTime;

    logCache(`Fetched recent elements from server`, {
      recentDaysCount,
      elementsCount: Object.keys(recentElements).length,
      fetchTimeMs: fetchTime,
    });

    // Load cache
    const cacheEntry = await loadCacheEntry(projectId, techCrewId);

    if (!cacheEntry || !isCacheValid(cacheEntry)) {
      logCache(`Cache miss or invalid - fetching all data from server`);

      // Get all elements and update cache
      const allElements = await getProjectTechCrewWorkdays(
        projectId,
        techCrewId,
        undefined,
        true,
      );

      // Update cache in background
      updateCache(projectId, techCrewId, allElements, recentDaysCount);

      const totalTime = Date.now() - startTime;
      logCache(`Completed with cache miss`, {
        totalElements: Object.keys(allElements).length,
        totalTimeMs: totalTime,
        fetchTimeMs: fetchTime,
      });

      return allElements;
    }

    // Cache hit - merge recent + cached older elements
    const mergeStartTime = Date.now();

    logCache(`Cache hit - merging recent + cached data`, {
      cachedOlderWorkdays: cacheEntry.olderWorkdayIds.length,
      cachedElements: Object.keys(cacheEntry.olderElements).length,
      recentElements: Object.keys(recentElements).length,
      cacheAge: Math.round(
        (Date.now() - cacheEntry.lastUpdated) / (1000 * 60 * 60),
      ), // hours
    });

    // Merge elements: recent takes precedence over cached for any overlapping elements
    // Start with only non-empty cached elements
    const mergedElements: ConrepAppWorkdayElements = {};

    // Add non-empty cached elements
    Object.entries(cacheEntry.olderElements).forEach(([elementId, actions]) => {
      if (actions && actions.length > 0) {
        mergedElements[elementId] = actions;
      }
    });

    // Add/overwrite with recent elements
    Object.entries(recentElements).forEach(([elementId, actions]) => {
      // Skip empty action arrays
      if (!actions || actions.length === 0) {
        return;
      }

      if (mergedElements[elementId]) {
        // Combine actions, removing any duplicates from cache that are in recent data
        const recentWorkdayIds = new Set(
          // (actions as ElementAction[]).map(action => typeof action.workday === 'string' ? action.workday : action.workday._id)
          actions.map((action) =>
            typeof action.workday === 'string'
              ? action.workday
              : action.workday._id,
          ),
        );

        const filteredCachedActions = mergedElements[elementId].filter(
          (action) => {
            const workdayId =
              typeof action.workday === 'string'
                ? action.workday
                : action.workday._id;
            return !recentWorkdayIds.has(workdayId);
          },
        );

        mergedElements[elementId] = [...filteredCachedActions, ...actions];
      } else {
        mergedElements[elementId] = actions;
      }
    });

    const mergeTime = Date.now() - mergeStartTime;
    const totalTime = Date.now() - startTime;

    // Update cache with any new data (if total workdays changed)
    if (totalWorkdays !== cacheEntry.totalWorkdaysWhenCached) {
      logCache(`Total workdays changed, updating cache`, {
        cached: cacheEntry.totalWorkdaysWhenCached,
        current: totalWorkdays,
      });
      updateCache(projectId, techCrewId, mergedElements, recentDaysCount);
    }

    logCache(`Completed with cache hit`, {
      totalElements: Object.keys(mergedElements).length,
      elementsFromCache: Object.keys(cacheEntry.olderElements).length,
      elementsFromServer: Object.keys(recentElements).length,
      fetchTimeMs: fetchTime,
      mergeTimeMs: mergeTime,
      totalTimeMs: totalTime,
    });

    return mergedElements;
  } catch (error) {
    logCache(`Error in getElementsByTechcrew_New`, error);

    // Fallback to original function
    logCache(`Falling back to original getElementsByTechcrew function`);
    try {
      return await getElementsByTechcrew(projectId, techCrewId, true);
    } catch (fallbackError) {
      logCache(`Fallback also failed`, fallbackError);
      throw fallbackError;
    }
  }
}

// Export utility functions for external use
export {
  getCacheStats,
  isLocalStorageAvailable,
  loadCacheEntry,
  setTestConfig,
};
